/**
 * Stripe subscription status → display.
 *
 * Seven statuses collapse to four things a shop needs to understand. The ACH
 * ones matter most: `incomplete` is the NORMAL state for days after a
 * successful Checkout, because ACH settles over ~3-5 business days. Showing a
 * raw enum there reads as a failure at the moment the user has just paid.
 *
 * ZERO IMPORTS BY DESIGN — unit-tested directly by
 * `scripts/test/subscriptionStatus.test.mjs`. Do not add imports.
 */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * 'YYYY-MM-DD...' -> '12 Sep 2026'.
 *
 * Parsed by hand, never through Date: `new Date('2026-09-12')` is UTC midnight
 * and renders as 11 Sep in HST/PST. See DATA_CONVENTIONS §3.2.
 */
export function formatPeriodDate(value) {
  if (!value) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value));
  if (!m) return '';
  const month = MONTHS[Number(m[2]) - 1];
  if (!month) return '';
  return `${Number(m[3])} ${month} ${m[1]}`;
}

function statusOf(sub) {
  return String((sub && sub.subscription_status) || '').trim().toLowerCase();
}

/**
 * A live subscription. `canceled` and absent both read false — both offer
 * Subscribe. `incomplete_expired` is also false: it is TERMINAL in Stripe
 * (the first payment never succeeded), so it must offer Subscribe again
 * rather than reading as an ongoing subscription.
 */
export function hasSubscription(sub) {
  const s = statusOf(sub);
  return s !== '' && s !== 'canceled' && s !== 'incomplete_expired';
}

/** Awaiting bank verification / ACH settlement. `incomplete_expired` is terminal, not pending. */
export function isPending(sub) {
  const s = statusOf(sub);
  return s === 'incomplete';
}

/** A charge has failed. `unpaid` is the terminal end of the same story. */
export function isPastDue(sub) {
  const s = statusOf(sub);
  return s === 'past_due' || s === 'unpaid';
}

export function statusLabel(sub) {
  const s = statusOf(sub);
  const periodEnd = formatPeriodDate(sub && sub.current_period_end);
  switch (s) {
    case '':
      return 'No subscription';
    case 'trialing':
      return periodEnd ? `Free trial · first charge ${periodEnd}` : 'Free trial';
    case 'active':
      return periodEnd ? `Active · renews ${periodEnd}` : 'Active';
    case 'incomplete':
      return 'Bank verification in progress';
    case 'incomplete_expired':
      return 'Signup did not complete';
    case 'past_due':
    case 'unpaid': {
      const since = formatPeriodDate(sub && sub.past_due_since);
      return since ? `Payment failed · since ${since}` : 'Payment failed';
    }
    case 'canceled':
      return 'Canceled';
    default:
      // Stripe may add statuses. Showing the raw value beats showing nothing.
      return s;
  }
}

export function statusVariant(sub) {
  const s = statusOf(sub);
  if (s === 'active') return 'success';
  if (s === 'trialing') return 'info';
  if (isPending(sub)) return 'warning';
  if (isPastDue(sub)) return 'danger';
  // incomplete_expired falls through to here — terminal, like canceled.
  return 'secondary';
}

/** 'YYYY-MM-DD...' -> 'YYYY-MM-DD', or '' when there is no date part. */
function ymdOf(value) {
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(String(value == null ? '' : value));
  return m ? m[1] : '';
}

/**
 * Jobvious has decided what this shop is on. Reads `assigned_tier`, never
 * `subscription_tier` — the latter is webhook-owned and only exists after
 * checkout, so it cannot express intent.
 */
export function hasAssignedPlan(sub) {
  return !!(sub && String(sub.assigned_tier || '').trim());
}

/**
 * Today as 'YYYY-MM-DD' in the VIEWER's local date. Built from local parts on
 * purpose: toISOString() is UTC and would roll to tomorrow after 14:00 in HST.
 */
export function todayYmd(now = new Date()) {
  const p2 = (n) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${p2(now.getMonth() + 1)}-${p2(now.getDate())}`;
}

/**
 * The assigned start date has already gone by — checkout will bill immediately
 * rather than waiting. String comparison, so no timezone can shift the answer.
 * Today itself is NOT past.
 */
export function isStartDatePast(sub, today = todayYmd()) {
  const d = ymdOf(sub && sub.assigned_start_date);
  return !!d && d < today;
}

/** The plan row for a tier at an interval, or null. */
export function planFor(plans, tier, interval) {
  if (!Array.isArray(plans) || !tier || !interval) return null;
  return plans.find(p => p && p.tier === tier && p.billing_interval === interval) || null;
}

/** Cents -> '$6,970.00'. Empty string for null/undefined, never 'NaN'. */
export function formatAmount(cents) {
  if (cents == null || Number.isNaN(Number(cents))) return '';
  return `$${(Number(cents) / 100).toLocaleString('en-US', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })}`;
}

/**
 * '$222.75 on 12 Sep 2026', or the date alone when the amount is unknown.
 *
 * Reads `current_period_end` — the authoritative date, held in our database and
 * therefore still correct when Stripe is unreachable. `next_payment_date` is
 * deliberately ignored: it is Unix SECONDS, so preferring it blanked this line
 * entirely (a truthy number short-circuits the fallback, then fails the
 * YYYY-MM-DD parse).
 *
 * The amount is `next_payment_amount_cents` from Stripe's upcoming invoice, NOT
 * the plan catalogue's list price — Checkout honours promo codes, so a
 * discounted shop would otherwise be shown a figure it will never be debited.
 * All three next_payment_* fields can be null on a live subscription, which is
 * not an error; the date alone is still worth showing.
 */
export function nextPaymentLabel(sub) {
  const when = formatPeriodDate(sub && sub.current_period_end);
  if (!when) return '';
  const amount = formatAmount(sub && sub.next_payment_amount_cents);
  return amount ? `${amount} on ${when}` : when;
}

/** 'Your first payment is due 12 Sep 2026', from the ASSIGNED start date. */
export function firstPaymentLabel(sub) {
  const when = formatPeriodDate(sub && sub.assigned_start_date);
  return when ? `Your first payment is due ${when}` : '';
}
